
import React, { useState, useCallback } from "react";
import ExperienceSelector from "@features/booking/components/ExperienceSelector";
import AvailableTimesList from "./AvailableTimesList";
import type { AlternativeMenu } from "./AvailableTimesList";
import CrossServiceAlternatives from "./CrossServiceAlternatives";
import PartnerRestaurantSuggestions from "./PartnerRestaurantSuggestions";
import NoAvailabilityCalendar from "./NoAvailabilityCalendar";
import { WalkInDialog } from "./WalkInDialog";
import { ExperienceSeparator } from "@features/booking/components/ExperienceSeparator";
import { BookingBreadcrumbs } from "./BookingBreadcrumbs";
import { EventBanner } from "./EventBanner";
import { EarlyAccessBanner } from "./EarlyAccessBanner";
import SelectTimeTitle from "./SelectTimeTitle";
import SelectTimeDescription from "./SelectTimeDescription";
import { TimeSlot } from "@features/booking";
import { useBooking } from "@features/booking/state";
import { useLanguage } from "@core/i18n";
import type { CrossServiceAlternative } from "@features/booking/hooks/useBookingFlow";
import type { PartnerSuggestion } from "./PartnerRestaurantSuggestions";
import type { ServiceType } from "@features/booking/state/types";
import type { MergedCalendarDay } from "@features/booking/types/calendar";

interface SelectTimeMainProps {
  showExperience: boolean;
  EXPERIENCES: { id: string; title: string }[];
  selectedExp: string;
  onExperienceChange: (value: string) => void;
  getSelectedExperienceLabel: () => string;
  availableTimes: TimeSlot[];
  selectedTime: string | null;
  handleSelectTime: (time: string) => void;
  loading?: boolean;
  onJoinWaitingList?: () => void;
  columnType?: string | null;
  allowWaitingList?: boolean;
  isWalkInDay?: boolean;
  isWaitingListDay?: boolean;
  centerHasOnlyPreferred?: boolean;
  isAuthenticated?: boolean;
  onPreferredLogin?: () => void;
  crossServiceAlternatives?: CrossServiceAlternative[];
  onSelectCrossServiceSlot?: (service: ServiceType, time: string) => void;
  calendarAvailability?: MergedCalendarDay[];
  isCalendarLoading?: boolean;
  onCalendarDateSelect?: (date: Date, service: ServiceType) => void;
  showNoAvailabilityCalendar?: boolean;
  calendarServiceTypes?: ServiceType[];
  currentService?: ServiceType | null;
  partnerSuggestions?: PartnerSuggestion[];
  alternativeMenus?: AlternativeMenu[];
  onSelectAlternativeMenu?: (menuId: number, time?: string) => void;
}

const SelectTimeMain = ({
  showExperience,
  EXPERIENCES,
  selectedExp,
  onExperienceChange,
  getSelectedExperienceLabel,
  availableTimes,
  selectedTime,
  handleSelectTime,
  loading = false,
  onJoinWaitingList,
  columnType,
  allowWaitingList = false,
  isWalkInDay = false,
  isWaitingListDay = false,
  centerHasOnlyPreferred = false,
  isAuthenticated = false,
  onPreferredLogin,
  crossServiceAlternatives = [],
  onSelectCrossServiceSlot,
  calendarAvailability = [],
  isCalendarLoading = false,
  onCalendarDateSelect,
  showNoAvailabilityCalendar = false,
  calendarServiceTypes = [],
  currentService,
  partnerSuggestions = [],
  alternativeMenus = [],
  onSelectAlternativeMenu,
}: SelectTimeMainProps) => {
  const hasAvailability = availableTimes.length > 0;
  const { diners, selectedDate } = useBooking();
  const { dateLocale } = useLanguage();
  const [walkInOpen, setWalkInOpen] = useState(false);

  const walkInDate = selectedDate ? new Date(selectedDate + 'T12:00:00') : null;
  const openWalkInDialog = useCallback(() => setWalkInOpen(true), []);
  const closeWalkInDialog = useCallback(() => setWalkInOpen(false), []);

  return (
  <>
    <div className="w-full px-3 sm:px-6 pt-6 pb-3 flex justify-center">
      <BookingBreadcrumbs />
    </div>
    <EventBanner />
    <main className="booking-content pt-4 pb-10">
      <div className="w-full max-w-md mx-auto px-6">
        {hasAvailability && (
          <>
            <SelectTimeTitle />
            <SelectTimeDescription />
          </>
        )}
        <EarlyAccessBanner />
        {showExperience && !loading && (
          <ExperienceSelector
            experiences={EXPERIENCES}
            selectedExp={selectedExp}
            onExperienceChange={onExperienceChange}
            getSelectedExperienceLabel={getSelectedExperienceLabel}
          />
        )}
        {hasAvailability && (
          <div className="flex justify-center my-6">
            <ExperienceSeparator />
          </div>
        )}
        <AvailableTimesList
          availableTimes={availableTimes}
          selectedTime={selectedTime}
          onSelectTime={handleSelectTime}
          loading={loading}
          columnType={columnType}
          allowWaitingList={allowWaitingList}
          isWalkInDay={isWalkInDay}
          isWaitingListDay={isWaitingListDay}
          onJoinWaitingList={onJoinWaitingList}
          centerHasOnlyPreferred={centerHasOnlyPreferred}
          isAuthenticated={isAuthenticated}
          onPreferredLogin={onPreferredLogin}
          alternativeMenus={alternativeMenus}
          onSelectAlternativeMenu={onSelectAlternativeMenu}
          onShowWalkInDetails={walkInDate ? openWalkInDialog : undefined}
        />
        {!loading && availableTimes.length === 0 && crossServiceAlternatives.length > 0 && onSelectCrossServiceSlot && (
          <CrossServiceAlternatives
            alternatives={crossServiceAlternatives}
            onSelectSlot={onSelectCrossServiceSlot}
          />
        )}
      </div>
      {showNoAvailabilityCalendar && onCalendarDateSelect && currentService && (
        <div className="w-full max-w-2xl mx-auto px-0 sm:px-6">
          <NoAvailabilityCalendar
            mergedCalendar={calendarAvailability}
            isLoading={isCalendarLoading}
            onDateSelect={onCalendarDateSelect}
            serviceTypes={calendarServiceTypes}
            currentService={currentService}
          />
        </div>
      )}
      {!loading && availableTimes.length === 0 && partnerSuggestions.length > 0 && (
        <div className="w-full max-w-2xl mx-auto px-6">
          <PartnerRestaurantSuggestions
            suggestions={partnerSuggestions}
          />
        </div>
      )}
      {walkInDate && (
        <WalkInDialog
          open={walkInOpen}
          onClose={closeWalkInDialog}
          date={walkInDate}
          partySize={diners}
          service={currentService}
          locale={dateLocale}
        />
      )}
    </main>
  </>
  );
};

export default React.memo(SelectTimeMain);
